import { useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import Button from "src/components/Button";
import Layout from "src/router/AuthenticatedRouter/Layout";
import Step1 from "./Step1";
import { Salary } from "src/components/Form/SalaryRange/SalaryRange";
import Step2 from "./Step2";
import Step3 from "./Step3";
import useLoader from "src/hooks/useLoader";
import { createJob, suggestJobSkils } from "src/network/job";
import {
  INTERVIEW_DURATION_OPTIONS,
  JOBTERM_OPTIONS,
  LOCATIONTYPE_OPTIONS,
  YES_NO_OPTIONS
} from "src/constants/formOptions";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import { experienceOptions } from "src/components/Form/DropdownSelect/Options";
import { setUsageConstraints } from "src/redux/slices/client";
import ConstraintModal from "src/components/Modal/ConstraintModal";
import { useLocation, useNavigate } from "react-router-dom";
import { defaultQuillValue } from "src/components/Form/QuillEditor/QuillEditor";
import { AtsJob } from "src/types/models/AtsJob";

interface SkillInput {
  name: string;
  for_interview: boolean;
}

const defaultValues = () => ({
  description: defaultQuillValue(),
  title: "",
  term: undefined as (typeof JOBTERM_OPTIONS)[0] | undefined,
  year_experience: undefined as number[] | undefined,
  location_type: undefined as (typeof LOCATIONTYPE_OPTIONS)[0] | undefined,
  city: "",
  experience_level: undefined as (typeof experienceOptions)[0] | undefined,
  interview_duration: undefined as (typeof INTERVIEW_DURATION_OPTIONS)[0] | undefined,
  visa_sponsor: YES_NO_OPTIONS[1],
  salary: {
    period: "Hourly",
    currency: "USD",
    salary: undefined
  } as Salary,
  ATS: undefined as Pick<AtsJob, "ats_id" | "ats_remote_id"> | undefined,
  technical_skills: [] as SkillInput[],
  intercultural_skills: [] as SkillInput[]
});

export type CreateJobValues = ReturnType<typeof defaultValues>;

const Steps = [Step1, Step2, Step3];

const CreateJobPage = () => {
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  let defVals = defaultValues();
  // If navigated here providing default values add them
  if (state?.default) defVals = { ...defVals, ...state.default };

  const navigate = useNavigate();
  const limit = useAppSelector(
    (r) => !!r.client.client?.Company.UsageConstraints?.active_jobs_exceeded
  );
  const [step, setStep] = useState(0);
  // Depending on the step the back buttton does something different
  let back = !step ? "/" : () => setStep(0);
  if (step === 2) back = undefined as any;
  const form = useForm({ defaultValues: defVals });

  const { handleSubmit, setValue } = form;
  const submit = async (data: CreateJobValues) => {
    // Step 0 goes to step 1
    if (!step) {
      // We need to fetch skills suggestions based on description
      try {
        const { technical, intercultural } = await suggestJobSkils({
          description: data.description.plaintext
        });
        if (!technical?.length) throw new Error("No beh skills");
        if (!intercultural?.length) throw new Error("No tech skills");

        // Haxes:
        // a) trim because gagan returns whitespaces from the endpoint :)
        // b) Romil asked to force first letter uppercase
        const hackSkillName = (name) => name.trim()[0].toUpperCase() + name.trim().slice(1);
        setValue(
          "technical_skills",
          technical.map((s, i) => ({ name: hackSkillName(s), for_interview: i < 2 }))
        );
        setValue(
          "intercultural_skills",
          intercultural.map((s, i) => ({ name: hackSkillName(s), for_interview: i < 2 }))
        );
      } catch (e) {
        console.error(e);
        throw new Error(
          "Error while analyzing job description, try to with a more accurate description"
        );
      }
      return setStep(1);
    }

    // Step 1 creates job
    const s = data.salary.salary;
    const job = {
      job_title: data.title,

      year_experience: data.year_experience as number[],
      experience_level: data.experience_level?.value,
      location_type: data.location_type?.value,
      description: data.description.plaintext,
      description_html: data.description._html(),
      visa_sponsor: data.visa_sponsor.value,
      city: data.city,
      term: data.term?.value,
      interview_duration: data.interview_duration?.value,
      technical_skills: data.technical_skills,
      intercultural_skills: data.intercultural_skills,
      ATS: data.ATS,
      Salary: s?.[0]
        ? {
            period: data.salary.period,
            currency: data.salary.currency,
            rate: s as any
          }
        : undefined
    };

    try {
      const j = await createJob(job);
      if (j.job) setStep(2);
      dispatch(setUsageConstraints(j.constraints));
    } catch (e) {
      console.error(e);
      throw new Error("Error occured on job creation");
    }
  };
  const { ldng: _submit, loading } = useLoader(submit);

  // The button changes depending on the step
  let Butn;
  if (step < 2)
    Butn = (
      <Button loading={loading} form="create_job" type="submit" shapeVariant={2}>
        {step ? "Post" : "Next"}
      </Button>
    );

  const Step = Steps[step];

  return (
    <Layout title="Post A Job" back={back} rightChildren={Butn}>
      <form
        id="create_job"
        onSubmit={handleSubmit(_submit)}
        className="bg-white min-h-full h-fit rounded-2xl border-[1px] border-light-gray p-9"
      >
        <Step form={form} />
      </form>

      <ConstraintModal variant="active_job_limit" open={limit} onClose={() => navigate("/")} />
    </Layout>
  );
};

export interface IStep {
  form: UseFormReturn<CreateJobValues>;
}
export default CreateJobPage;
